'use client';

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type StatusTone = 'success' | 'warning' | 'danger' | 'muted';
export type ToastTone = 'ok' | 'error';

const TONE_CLASS: Record<StatusTone, string> = {
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  muted: 'border-line bg-shell text-muted',
};

const DeskToastContext = createContext<(message: string, tone?: ToastTone) => void>(() => {});

export function useDeskToast() {
  return useContext(DeskToastContext);
}

export function DeskToastProvider({
  notify,
  toast,
  children,
}: {
  notify: (message: string, tone?: ToastTone) => void;
  toast: { message: string; tone: ToastTone } | null;
  children: ReactNode;
}) {
  return (
    <DeskToastContext.Provider value={notify}>
      {children}
      {toast && (
        <p
          role="status"
          className={`fixed bottom-4 end-4 z-50 max-w-sm rounded-xl border bg-card px-4 py-3 text-sm shadow-xl ${toast.tone === 'error' ? 'border-danger/40 text-danger' : 'border-success/40 text-success'}`}
        >
          {toast.message}
        </p>
      )}
    </DeskToastContext.Provider>
  );
}

export function useDeskNotify() {
  const [toast, setToast] = useState<{ message: string; tone: ToastTone } | null>(null);
  const notify = useCallback((message: string, tone: ToastTone = 'ok') => {
    setToast({ message, tone });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return { toast, notify };
}

export function statusToneClass(tone: StatusTone): string {
  return TONE_CLASS[tone];
}

export function StatusChip({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusToneClass(tone)}`}>{children}</span>;
}

export function bookingTone(status: string): StatusTone {
  if (status === 'pending') return 'warning';
  if (status === 'confirmed' || status === 'active') return 'success';
  if (status === 'cancelled') return 'danger';
  return 'muted';
}

export function vehicleTone(status: string): StatusTone {
  if (status === 'available' || status === 'rented') return 'success';
  if (status === 'maintenance' || status === 'reserved') return 'warning';
  return 'muted';
}

export function riskTone(level: string): StatusTone {
  if (level === 'clean') return 'success';
  if (level === 'watch') return 'warning';
  if (level === 'blacklisted') return 'danger';
  return 'muted';
}

export function fineTone(status: string): StatusTone {
  if (status === 'paid') return 'success';
  if (status === 'disputed') return 'warning';
  return 'danger';
}

export function complianceDotClass(state: string): string {
  if (state === 'ok') return 'bg-success';
  if (state === 'soon') return 'bg-warning';
  if (state === 'expired') return 'bg-danger';
  return 'bg-line';
}
